/**
 * 系统维护弹窗（mr），从 app utils 迁入
 */
const ID = {
  overlay: 'mr-overlay',
  dialog: 'mr-dialog',
  dialogHeader: 'mr-dialog_header',
  dialogContent: 'mr-dialog_content',
  tip: 'mr-dialog_content-tip',
  btn: 'mr-dialog_content-btn',
} as const

type MrDictionary = {
  title: string
  content: string
  btn: string
}

type MrLanguage = 'zhCn' | 'zhTc'

interface MrConfig {
  confirm?: () => void
  JSBridge?: { close: () => void } | null
  confirmButtonVisible?: boolean
}

const isMobile = () =>
  /android|webos|iphone|ipod|balckberry/.test(navigator.userAgent.toLowerCase())

const mobile = (dictionary: MrDictionary, confirmButtonVisible: boolean) => `
<div id=${ID.dialog} style="position: absolute;top: 50%;left: 50%;transform: translateX(-50%) translateY(-50%);width:2.8rem;background-color: #fff;border-radius: 16px;overflow: hidden">
    <div id=${ID.dialogHeader} style="height: 56px;width: 100%;line-height: 56px;color: #181818;font-weight: 600;font-size: 16px;text-align: center">${dictionary.title}</div>
    <div id=${ID.dialogContent} style="font-size: 14px; line-height: 18px; color: #666; padding: 0px 16px 24px;">
        ${dictionary.content}
        ${confirmButtonVisible ? `<button id=${ID.btn} style="margin-top: 8px;background-color: #FF6907;height: 44px;color: #fff;text-indent: 0px;text-shadow: none;border-radius: 22px;width: 100%;border: none">${dictionary.btn}</button>` : ''}
    </div>
</div>
`

const pc = (dictionary: MrDictionary, confirmButtonVisible: boolean) => `
<div id=${ID.dialog} style="position: absolute;top: 50%;left: 50%;transform: translateX(-50%) translateY(-50%);width:280px;background-color: #fff;border-radius: 16px;overflow: hidden">
    <div id=${ID.dialogHeader} style="height: 56px;width: 100%;line-height: 56px;color: #181818;font-weight: 600;font-size: 16px;text-align: center">${dictionary.title}</div>
    <div id=${ID.dialogContent} style="font-size: 14px; line-height: 18px; color: #666; padding: 0px 16px 24px;">
        ${dictionary.content}
        ${confirmButtonVisible ? `<button id=${ID.btn} style="margin-top: 8px;background-color: #FF6907;height: 44px;color: #fff;text-indent: 0px;text-shadow: none;border-radius: 22px;width: 100%;border: none;cursor: pointer">${dictionary.btn}</button>` : ''}
    </div>
</div>
`

const i18nDictionary = {
  zhCn: {
    title: '系统维护提示',
    content: `<section id=${ID.dialogContent} style="font-weight: 400;font-size: 14px;line-height: 22px;text-align: justify"><p>为提升系统体验，<span style="color:#2f2f2f;font-weight:600">$time</span>进行系统维护。</p><p style="margin-top:16px">开户、出入金、换汇、提交订单等操作将受影响无法使用。同时，系统可能不能正常查询您的资金、持仓、交易历史等，待系统维护后会恢复正常。如对阁下造成不便，为此深表歉意。</p></section>`,
    btn: '关闭',
  },
  zhTc: {
    title: '系統維護提示',
    content: `<section id=${ID.dialogContent} style="font-weight: 400;font-size: 14px;line-height: 22px;text-align: justify"><p>為提升系統體驗，<span style="color:#2f2f2f;font-weight:600">$time</span>進行系統維護。</p><p style="margin-top:16px">開戶、出入金、換匯、提交訂單等操作將受影響無法使用。同時，系統可能不能正常查詢您的資金、持倉、交易歷史等，待系統維護後會恢復正常。如對閣下造成不便，為此深表歉意。</p></section>`,
    btn: '關閉',
  },
} satisfies Record<MrLanguage, MrDictionary>

const close = (JSBridge: MrConfig['JSBridge']) => {
  if (JSBridge) {
    JSBridge.close()
    return
  }
  const userAgent = navigator.userAgent
  if (userAgent.indexOf('Firefox') !== -1 || userAgent.indexOf('Chrome') !== -1) {
    window.location.href = 'about:blank'
  } else if (userAgent.indexOf('Android') > -1 || userAgent.indexOf('Linux') > -1) {
    window.opener = null
    window.open('about:blank', '_self', '')?.close()
  } else {
    window.opener = null
    window.open('about:blank', '_self')
    window.close()
  }
}

function resolveLanguage(lang: string): MrLanguage {
  return lang === 'zhTc' ? 'zhTc' : 'zhCn'
}

/**
 * 系统维护弹窗
 * @param selector - 挂载选择器
 * @param language - zhCn | zhTc
 * @param label - 如 { time: '...' }
 * @param config - confirm / JSBridge / confirmButtonVisible
 */
export default function mr(
  selector: string,
  language: MrLanguage | string = 'zhCn',
  label: Record<string, string> = {},
  config: MrConfig = {}
) {
  const { confirm = () => {}, JSBridge = null, confirmButtonVisible = false } = config
  const app = document.querySelector(selector)
  if (!app) {
    return
  }

  const lang = resolveLanguage(language || 'zhCn')
  const dictionary: MrDictionary = JSON.parse(JSON.stringify(i18nDictionary[lang]))

  const keys: (keyof MrDictionary)[] = ['title', 'content', 'btn']
  for (const key of keys) {
    Object.entries(label).forEach(([labelKey, labelValue]) => {
      dictionary[key] = dictionary[key].replace(new RegExp(`\\$${labelKey}`, 'g'), labelValue)
    })
  }
  if (!document.getElementById(ID.overlay)) {
    const overlay = document.createElement('div')
    overlay.id = ID.overlay
    overlay.style.cssText =
      'z-index: 3000;position: fixed;top: 0;left: 0;width: 100%;height: 100%;background-color: rgba(0, 0, 0, 0.3);'
    overlay.innerHTML = isMobile()
      ? mobile(dictionary, confirmButtonVisible)
      : pc(dictionary, confirmButtonVisible)
    if (confirmButtonVisible) {
      const btnEl = overlay.querySelector(`#${ID.btn}`)
      const clickHandle = () => {
        confirm()
        close(JSBridge)
        btnEl?.removeEventListener('click', clickHandle)
        app.removeChild(overlay)
      }
      btnEl?.addEventListener('click', clickHandle)
    }
    app.appendChild(overlay)
  }
}
